import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Globe, Tag, Info, Database, Search, Shield, Radio, Clock, ExternalLink, Copy, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AssetDetailTabs } from '../../components/assets/AssetDetailTabs';
import { AssetGraph } from '../../components/graphs/AssetGraph';
import { TechnologyStack } from '../../components/assets/TechnologyStack';
import { AssetKeyInfo } from '../../components/assets/detail/AssetKeyInfo';
import { AssetWhois } from '../../components/assets/detail/AssetWhois';
import { AssetDNSRecords } from '../../components/assets/detail/AssetDNSRecords';
import { AssetServices } from '../../components/assets/detail/AssetServices';
import { AssetTLSCertificate } from '../../components/assets/detail/AssetTLSCertificate';
import { AssetRecentFindings } from '../../components/assets/detail/AssetRecentFindings';
import { AssetPorts } from '../../components/assets/detail/AssetPorts';
import { AssetFindings } from '../../components/assets/detail/AssetFindings';
import { AssetEvents } from '../../components/assets/detail/AssetEvents';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';
import { AssetRiskScoreCard } from '../../components/assets/detail/AssetRiskScoreCard';
import { AssetsApi, Domain, Port, FindingsApi, TechnologiesApi, Finding, Technology, PaginatedPortList, DomainFromJSONTyped, DomainFromJSON, DomainToJSONTyped, DomainToJSON, PaginatedPortListFromJSONTyped } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { Timeline } from "@/components/Timeline";

// API will be initialized with auth token

interface DomainData {
  domain: any | null;
  findings: {
    results: Finding[];
    count: number;
    next?: string | null;
    previous?: string | null;
  };
  technologies: Technology[] | null;
  ports: Port[] | null;
  loading: boolean;
  error: string | null;
}

const initialDomainData: DomainData = {
  domain: null,
  findings: {
    results: [],
    count: 0,
    next: null,
    previous: null
  },
  technologies: null,
  ports: [],
  loading: true,
  error: null
};



const generateAssetGraphData = (domain: Domain | null, data: DomainData) => {
  if (!domain) return { nodes: [], edges: [] };
  
  const nodes = [
    { id: 'domain', label: domain.domainName || '', type: 'domain', risk: domain.riskScore?.toUpperCase() || 'low' }
  ];
  
  const edges: any[] = [];

  // Add IP nodes and edges
  if (domain.ipAddresses?.length > 0) {
    domain.ipAddresses.forEach((ipAddress, index) => {
      const ipId = `ip_${index}`;
      nodes.push({
        id: ipId,
        label: ipAddress.toString(),
        type: 'ip',
        risk: 'low'
      });
      edges.push({ source: 'domain', target: ipId, type: 'resolves_to' });
    });
  }

  // Add port nodes and edges from real data
  if (data.ports) {
    data.ports.forEach((port, index) => {
      const portId = `port_${index}`;
      nodes.push({
        id: portId,
        label: `Port ${port.number} (${port.service || 'Unknown'})`,
        type: 'port',
        risk: 'low'
        // risk: port.riskScore?.toUpperCase() || 'low'
      });
      edges.push({ source: 'ip', target: portId, type: 'exposes' });
    });
  }

  // Add findings as vulnerability nodes
  if (data.findings?.results) {
    data.findings?.results.forEach((finding, index) => {
      const findingId = `finding_${index}`;
      nodes.push({
        id: findingId,
        label: finding.title || 'Unknown Finding',
        type: 'vulnerability',
        risk: finding.severity || 'low'
      });
      // Connect findings to domain
      edges.push({ source: 'domain', target: findingId, type: 'vulnerable_to' });
    });
  }

  // Add technologies
  if (data.technologies) {
    data.technologies.forEach((tech, index) => {
      const techId = `tech_${index}`;
      nodes.push({
        id: techId,
        label: tech.name,
        type: 'service',
        risk: 'low'
      });
      edges.push({ source: 'domain', target: techId, type: 'uses' });
    });
  }

  return { nodes, edges };
};

const AssetDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState<DomainData>(initialDomainData);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchDomainData = async () => {
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const assetsApi = new AssetsApi(getApiConfiguration(token));
        const technologiesApi = new TechnologiesApi(getApiConfiguration(token));

        const [domainResponse, portsResponse, technologiesResponse] = await Promise.all([
          assetsApi.domainsRetrieve({ id, teamSlug: currentTeam.slug }),
          assetsApi.assetsPortsRetrieve({ assetId: parseInt(id), assetType: 'domain', teamSlug: currentTeam.slug }),
          technologiesApi.technologiesAssetsList({ assetId: parseInt(id), assetType: 'domain', teamSlug: currentTeam.slug })
        ]);

        // Log the domain response to understand its structure
        console.log('Domain Response:', domainResponse);
        
        // Parse the domain response to match the expected Domain type
        const parsedDomain: Domain = domainResponse;
        
        setData(prev => ({
          domain: parsedDomain,
          ports: portsResponse ? (portsResponse as unknown as PaginatedPortList).results : null ,
          findings: prev.findings, // Preserve existing findings
          technologies: technologiesResponse,
          loading: false,
          error: null
        }));
      } catch (error) {
        console.error('Error fetching domain data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch domain data'
        }));
      }
    };

    fetchDomainData();
  }, [id, currentTeam?.slug, token]);

  // Define a type for the findings filters
  interface FindingsFiltersState {
    search: string;
    severity: string | undefined;
    status: string | undefined;
    createdLast: number | undefined;
  }

  // Separate effect for fetching findings
  const [findingsFilters, setFindingsFilters] = useState<FindingsFiltersState>({
    search: '',
    severity: undefined,
    status: undefined,
    createdLast: undefined
  });

  // Create a wrapper function to handle the type mismatch
  const handleFilterChange = useCallback((filters: {
    search?: string;
    severity?: string;
    status?: string;
    createdLast?: number;
  }) => {
    setFindingsFilters(prevFilters => ({
      ...prevFilters,
      // Ensure search is always a string, even if undefined is passed > test build
      search: filters.search ?? prevFilters.search,
      severity: filters.severity,
      status: filters.status,
      createdLast: filters.createdLast
    }));
  }, []);

  useEffect(() => {
    const fetchFindings = async () => {
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const findingsApi = new FindingsApi(getApiConfiguration(token));
        const findingsResponse = await findingsApi.assetFindingsList({ 
          assetId: parseInt(id), 
          assetType: 'domain', 
          teamSlug: currentTeam.slug,
          page: currentPage,
          ...findingsFilters
        });

        setData(prev => ({
          ...prev,
          findings: findingsResponse
        }));
      } catch (error) {
        console.error('Error fetching findings:', error);
      }
    };

    fetchFindings();
  }, [id, currentTeam?.slug, token, currentPage, findingsFilters]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  if (data.loading) {
    return <div>Loading...</div>;
  }

  if (data.error || !data.domain) {
    return <div>Error: {data.error || 'Domain not found'}</div>;
  }

  const domain = data.domain;
  const ports = domain.ports;
  const { results: findings, next: findingsNext, count: findingsCount, previous: findingsPrevious } = data.findings;
  const technologies = data.technologies;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Content - 2 columns */}
              <div className="col-span-2 space-y-6">

                <AssetDNSRecords records={domain.dnsRecords || []} />
                <AssetServices services={domain.ports || []} />
                <AssetRecentFindings findings={findings.slice(0, 3)} />
              </div>

              {/* Sidebar - 1 column */}
              <div className="space-y-6">
                <AssetWhois data={domain.whoisRecords || {}} />
                <AssetTLSCertificate certificate={domain.tlsCertificate || {}} />
              </div>
            </div>
          </div>
        );
      case 'graph':
        return (
          <div className="space-y-6">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
              {/* Graph Controls */}
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm">
                      <Maximize2 className="h-4 w-4 mr-2" />
                      Zoom to Fit
                    </Button>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-400">Layout:</label>
                      <select className="bg-gray-800/30 border border-gray-800 rounded-lg text-sm text-white px-2 py-1">
                        <option>Force-Directed</option>
                        <option>Hierarchical</option>
                        <option>Circular</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-xs text-gray-400">Critical</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-orange-500" />
                      <span className="text-xs text-gray-400">High</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-400">Medium</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      <span className="text-xs text-gray-400">Low</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph Canvas */}
              <div className="p-6">
                <div className="w-full h-[600px] rounded-lg bg-[#0F1629]">
                  <AssetGraph nodes={generateAssetGraphData(domain, data).nodes} links={generateAssetGraphData(domain, data).edges} width={800} height={600} />
                </div>
              </div>
            </div>

            {/* Graph Statistics */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Graph Statistics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Nodes</span>
                    <span className="text-sm text-white">{generateAssetGraphData(domain, data).nodes.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Edges</span>
                    <span className="text-sm text-white">{generateAssetGraphData(domain, data).edges.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Connected Components</span>
                    <span className="text-sm text-white">1</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Node Distribution</h3>
                <div className="space-y-4">
                  {['domain', 'ip', 'port', 'service', 'vulnerability', 'certificate'].map(type => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-sm text-gray-400 capitalize">{type}s</span>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(domain, data).nodes.filter(n => n.type === type).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
                <div className="space-y-4">
                  {['critical', 'high', 'medium', 'low'].map(risk => (
                    <div key={risk} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className={`h-3 w-3 rounded-full ${
                          risk === 'critical' ? 'bg-red-500' :
                          risk === 'high' ? 'bg-orange-500' :
                          risk === 'medium' ? 'bg-yellow-500' :
                          'bg-emerald-500'
                        }`} />
                        <span className="text-sm text-gray-400 capitalize">{risk}</span>
                      </div>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(domain, data).nodes.filter(n => n.risk === risk).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'ports':
        return <AssetPorts ports={typeof domain.ports === 'object' ? domain.ports : []} />;

      case 'findings':
        return (
          <AssetFindings 
            findings={findings} 
            paginationInfo={{
              count: findingsCount,
              next: findingsNext || null,
              previous: findingsPrevious || null
            }}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            itemsPerPage={10}
            onFilterChange={handleFilterChange}
          />
        );
      case 'events':
        return <AssetEvents entries={domain.history} />;
      case 'notes':
        return <AssetNotes objectId={domain.id?.toString()} objectType="domain" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Globe className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{domain.domainName}</h1>
                <Badge variant="active">{domain?.status || 'Unknown'}</Badge>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    {domain.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>Last scan: {new Date(domain?.latestScan?.created_at || '').toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
              <Copy className="h-4 w-4" />
            </button>
            <a
              href={`https://${domain.domainName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4 mt-6">
          <AssetRiskScoreCard riskScore={domain?.riskScore?.toUpperCase() || 'low'} findings={domain?.findingsStats || {}} />
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Open Ports</div>
              <Radio className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{domain.ports.length}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Active Findings</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{domain?.findingsStats && "total" in domain?.findingsStats ? domain?.findingsStats.total : 0}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Techologies</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <TechnologyStack
                    technologies={domain.technologies}
                    maxVisible={3}
                  />
          </div>
          
 
        </div>
      </div>

      {/* Tabs */}
      <AssetDetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        assetId={domain.id?.toString() || ''}

        portsCount={ports.length}
        findingsCount={domain?.findingsStats && "total" in domain?.findingsStats ? domain?.findingsStats.total : 0}
        eventsCount= {domain.history.length}
      />

      {/* Tab Content */}
      {renderTabContent()}

    </div>
  );
};

export default AssetDetail;